/**
 * The customer's FIRST administrator, before anyone at the customer can act
 * (customer onboarding part B). Shown only while the customer has no active
 * administrator and only to a signed-in staff member: the server records that
 * person as the actor and refuses a shared operator secret. Once a customer
 * administrator is active, people are the customer's to manage.
 *
 * Customer onboarding; deliberately not under Staff & Access.
 */
import { useState } from 'react';
import type { ConsoleClient } from '../platform/client.js';
import type { ConsoleRequest } from '../platform/requests.js';
import type { PlatformActor } from '../platform/credentials.js';
import { isStaffToken } from '../platform/staffSession.js';
import type { CustomerReadinessRead } from '../platform/tenancy.js';
import { ConfirmDialog, type PendingConfirmation } from './ConfirmDialog.js';

export const firstAdminRequests = {
  resend: (orgId: string): ConsoleRequest => ({
    method: 'POST',
    path: `/api/system/customers/${encodeURIComponent(orgId)}/administrator/resend`,
  }),
  replace: (orgId: string, name: string, email: string): ConsoleRequest => ({
    method: 'POST',
    path: `/api/system/customers/${encodeURIComponent(orgId)}/administrator/replace`,
    body: { name, email },
  }),
};

export function FirstAdministrator({
  orgId,
  readiness,
  actor,
  client,
  onChanged,
}: {
  orgId: string;
  readiness: CustomerReadinessRead['readiness'];
  actor: PlatformActor;
  client: ConsoleClient;
  onChanged: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [confirming, setConfirming] = useState<PendingConfirmation | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  if (readiness.firstAdmin.activeAdmins > 0) return null;
  const staff = isStaffToken(actor.systemToken);

  const run = async (request: ConsoleRequest, success: string) => {
    const r = await client<{ delivery?: string }>(request, actor);
    setNotice(
      r.kind === 'ok'
        ? { ok: true, text: `${success} Email: ${r.value.delivery ?? 'recorded'}.` }
        : { ok: false, text: r.message },
    );
    if (r.kind === 'ok') onChanged();
  };

  return (
    <section>
      <ConfirmDialog pending={confirming} onClose={() => setConfirming(null)} />
      <h3>First administrator</h3>
      <p className="quiet">
        Nobody at this customer is an active administrator yet. Invitation:{' '}
        <strong>{readiness.firstAdmin.invitation.replace(/_/g, ' ')}</strong>
        {readiness.firstAdmin.invitationExpiresAt
          ? ` · expires ${new Date(readiness.firstAdmin.invitationExpiresAt).toLocaleString()}`
          : ''}
        .
      </p>
      {!staff ? (
        <p className="quiet">
          Sign in as yourself to resend or replace this invitation. An operator credential cannot
          change who administers a customer.
        </p>
      ) : (
        <>
          {notice ? (
            <div
              className={notice.ok ? 'banner ok' : 'banner'}
              role={notice.ok ? 'status' : 'alert'}
            >
              {notice.text}
            </div>
          ) : null}
          <p>
            <button
              type="button"
              disabled={
                readiness.firstAdmin.invitation !== 'pending' &&
                readiness.firstAdmin.invitation !== 'pending_past_expiry'
              }
              onClick={() =>
                setConfirming({
                  title: 'Resend the invitation?',
                  body: 'A new link is emailed to the invited address and the previous link stops working. It is valid for 7 days.',
                  confirmLabel: 'Resend',
                  onConfirm: () => void run(firstAdminRequests.resend(orgId), 'Invitation resent.'),
                })
              }
            >
              Resend invitation
            </button>
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setConfirming({
                title: `Make ${email} the first administrator?`,
                body: 'Every pending first-administrator invitation is revoked and the person it was for can no longer use it. A new invitation is emailed to this address. You are recorded as the person who did this, in the customer’s own audit history.',
                confirmLabel: 'Replace administrator',
                danger: true,
                onConfirm: () =>
                  void run(
                    firstAdminRequests.replace(orgId, name.trim(), email.trim()),
                    'Administrator replaced.',
                  ),
              });
            }}
          >
            <label htmlFor="faName">Replace with — name</label>
            <input id="faName" value={name} onChange={(e) => setName(e.target.value)} />
            <label htmlFor="faEmail">Email</label>
            <input
              id="faEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p>
              <button type="submit" disabled={!name.trim() || !email.trim()}>
                Replace administrator
              </button>
            </p>
          </form>
        </>
      )}
    </section>
  );
}
